/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ISearchFormViewProps } from './components';
import { ISearchSuggestionModalViewProps, ISearchViewProps } from './mil-search';

const SearchView: React.FC<ISearchViewProps> = props => {
    const {
        Search,
        AutoSuggestAriaLabel,
        AutoSuggestAriaLabelText,
        searchText,
        AutoSuggest,
        form,
        SearchForm,
        FormWrapper,
        label,
        searchSuggestionsProducts,
        searchForText,
        productsCount,
        renderError,
        isMobile,
        milSearchSuggestionModal
    } = props;

    if (isMobile) {
        return (
            <Module {...Search}>
                {label}
                {createSearchResultModal(
                    AutoSuggest,
                    searchText,
                    AutoSuggestAriaLabel,
                    milSearchSuggestionModal,
                    AutoSuggestAriaLabelText,
                    searchForText,
                    productsCount,
                    renderError,
                    searchSuggestionsProducts,
                    form as ISearchFormViewProps,
                    SearchForm,
                    FormWrapper,
                    isMobile
                )}
            </Module>
        );
    }

    return (
        <Module {...Search}>
            {label}
            {_renderForm(form as ISearchFormViewProps, SearchForm, FormWrapper, isMobile)}
            <Node {...AutoSuggest}>
                {searchText && searchText.length > 0 ? <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''}
                {searchText && searchText.length > 0 ? renderTitle(searchForText, searchText) : null}
                {productsCount === 0 ? renderError : null}
                {searchSuggestionsProducts}
            </Node>
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    AutoSuggest: INodeProps,
    searchText: string,
    AutoSuggestAriaLabel: INodeProps,
    milSearchSuggestionModal: ISearchSuggestionModalViewProps,
    AutoSuggestAriaLabelText: string,
    searchForText: string,
    productsCount: number,
    renderError: JSX.Element | null,
    searchSuggestionsProducts: React.ReactNode,
    form: React.ReactNode,
    SearchForm: INodeProps,
    FormWrapper: INodeProps,
    isMobile: boolean
): JSX.Element => {
    return React.cloneElement(
        milSearchSuggestionModal.modal,
        {},
        milSearchSuggestionModal.modalHeader,
        createModalBody(
            AutoSuggest,
            searchText,
            AutoSuggestAriaLabel,
            milSearchSuggestionModal,
            AutoSuggestAriaLabelText,
            searchForText,
            productsCount,
            renderError,
            searchSuggestionsProducts,
            form as ISearchFormViewProps,
            SearchForm,
            FormWrapper,
            isMobile
        ),
        milSearchSuggestionModal.modalFooter
    );
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    AutoSuggest: INodeProps,
    searchText: string,
    AutoSuggestAriaLabel: INodeProps,
    milSearchSuggestionModal: ISearchSuggestionModalViewProps,
    AutoSuggestAriaLabelText: string,
    searchForText: string,
    productsCount: number,
    renderError: JSX.Element | null,
    searchSuggestionsProducts: React.ReactNode,
    form: React.ReactNode,
    SearchForm: INodeProps,
    FormWrapper: INodeProps,
    isMobile: boolean
): JSX.Element | null => {
    return (
        <>
            {_renderForm(form as ISearchFormViewProps, SearchForm, FormWrapper, isMobile)}
            <Node {...AutoSuggest}>
                {searchText && searchText.length > 0 ? <Node {...AutoSuggestAriaLabel}>{AutoSuggestAriaLabelText}</Node> : ''}
                {searchText && searchText.length > 0 ? renderTitle(searchForText, searchText) : null}
                {productsCount === 0 ? renderError : null}
                {searchSuggestionsProducts}
            </Node>
        </>
    );
};

const _renderForm = (form: ISearchFormViewProps, SearchForm: INodeProps, FormWrapper: INodeProps, isMobile: boolean) => {
    return (
        <Node {...SearchForm}>
            <Node {...FormWrapper}>
                {form.input}
                {!isMobile && form.cancelBtn}
                {!isMobile && form.submitBtn}
            </Node>
        </Node>
    );
};

/**
 * Render Title for AutoSuggest.
 * @param searchForText -The heading resource string.
 * @param searchText -The search string.
 * @returns -The JSX Element.
 */
const renderTitle = (searchForText: string, searchText: string): JSX.Element => {
    const searchTextString = '"'.concat(searchText.concat('"'));
    const searchAutoSuggestionHeading = searchForText ? `${searchForText} ${searchTextString}` : `Search for ${searchTextString}`;

    return <Node className='msc-autoSuggest__search-title'>{searchAutoSuggestionHeading}</Node>;
};

export default SearchView;
